import {
  Observable,
  Subject,
  Subscription,
  distinctUntilChanged,
  BehaviorSubject,
} from 'rxjs';

import { pauseable, takeIf } from './rxjs/operators';
import { AuthEvent, AuthStatus } from './auth-event';
import { v4 as uuid } from 'uuid';

const _subscriptions: Subscription[] = [];
let _requestLogin: (() => void) | null = null;
let _auth$: Observable<AuthEvent> | null;
let lastAuthEvent: AuthEvent | null = null;

const _fetchJobStream$ = new Subject<FetchJob>();

function doFetchJob(fetchJob: FetchJob) {
  const currentAccessToken = lastAuthEvent?.state?.access_token || '';
  fetchJob.fetchCallback(currentAccessToken).catch(() => {
    if (fetchJob.retryCount > 0) {
      fetchJob.retryCount--;
      setTimeout(() => _fetchJobStream$.next(fetchJob), 2000);
    }
  });
}

type AsyncFetchCallback = (bearerToken: string) => Promise<void>;
interface FetchJobOption {
  fetchCallback: AsyncFetchCallback;
  failedCallback: (error: string | any) => void;
  retryCount: number;
  id?: string;
}

export class FetchJob {
  public fetchCallback: AsyncFetchCallback;
  public failedCallback: (error: string | any) => void;
  public retryCount!: number;
  public id: string | null = null;
  public constructor(option: FetchJobOption) {
    this.id = option.id || uuid();
    this.fetchCallback = option.fetchCallback;
    this.failedCallback = option.failedCallback;
    this.retryCount = option.retryCount;
  }
}

function isAuthenticated(authEvent: AuthEvent) {
  return authEvent.authStatus === AuthStatus.AUTHENTICATED;
}
function accessToken(authEvent: AuthEvent) {
  return authEvent.state?.access_token;
}

export function setupClassCommon() {
  trySetAuthStream((globalThis as any).ClassCommon?.OpenID?.event$);

  // // Below code simulates unreliable network/access_token
  // const authEvent: AuthEvent = { authStatus: "AUTHENTICATED", state: { access_token: "init-token" } }
  // const auth$ = new BehaviorSubject(authEvent)
  // const access_token = "place your token here";
  // setTimeout(
  //   () => setInterval(() => auth$.next( { authStatus: "AUTHENTICATED", state: { access_token } }), 100),
  //   250
  // );
  // trySetAuthStream(auth$);
}

export function trySetAuthStream(
  authStream$: Observable<AuthEvent>,
  requestLogin: (() => void) | null = null
) {
  if (hasAuthStream()) {
    return false; // already got auth stream
  }
  setAuthStream(authStream$, requestLogin);
  return true;
}

export function hasAuthStream() {
  return !!_auth$;
}

export function unsetClassCommon() {
  if (!hasAuthStream()) {
    return false; // auth stream no set
  }
  // clean up current subscriptions
  _subscriptions.map((s) => s.unsubscribe());
  _subscriptions.splice(0, _subscriptions.length);
  _auth$ = null;
  _requestLogin = null;
  return true;
}

function setAuthStream(
  authStream$: Observable<AuthEvent>,
  requestLogin: (() => void) | null = null
) {
  // clean up current subscriptions
  _subscriptions.map((s) => s.unsubscribe());
  _subscriptions.splice(0, _subscriptions.length);

  // persist variables in memory
  _auth$ = authStream$;
  _requestLogin = requestLogin;

  if (!_auth$) return;
  const reRequestLogin = !!_requestLogin
    ? _requestLogin
    : () => (globalThis as any).ClassCommon?.OpenID?.instance?.signinSilent();

  const predicateNotAuthenticated = (authEvent: AuthEvent): boolean =>
    !isAuthenticated(authEvent);

  // wire up
  _subscriptions.push(
    _auth$
      .pipe(
        distinctUntilChanged(
          (p, c) => p.state?.access_token === c.state?.access_token
        )
      )
      .subscribe((e) => (lastAuthEvent = e))
  );
  _subscriptions.push(
    _fetchJobStream$
      .pipe(takeIf(_auth$, predicateNotAuthenticated))
      .subscribe(reRequestLogin)
  );
  _subscriptions.push(
    _fetchJobStream$
      .pipe(
        pauseable(_auth$, predicateNotAuthenticated),
        distinctUntilChanged((p, c) => p.id === c.id)
      )
      .subscribe(doFetchJob)
  );
}

export function fetchFromClass(fetchJob: FetchJob) {
  if (!_auth$) {
    // eslint-disable-next-line no-throw-literal
    throw 'Missing Authentication';
  }

  _fetchJobStream$.next(fetchJob);
}
